import type {
  CreatePayPeriodInput,
  ExportPayPeriodInput,
  ExportPayPeriodResponse,
  PayPeriodDetail,
  PayPeriodReview,
  PayPeriodSummary,
  RecalculatePayPeriodResponse,
  RequestCorrectionInput,
  UnlockPayPeriodInput,
} from "@crewledger/shared/schemas";
import { apiFetch } from "@/lib/api";

export async function fetchPayPeriods(params?: {
  page?: number;
  pageSize?: number;
  status?: string;
}) {
  const search = new URLSearchParams();
  if (params?.page) search.set("page", String(params.page));
  if (params?.pageSize) search.set("pageSize", String(params.pageSize));
  if (params?.status) search.set("status", params.status);

  const qs = search.toString();
  return apiFetch<{ data: PayPeriodSummary[]; meta: { total?: number } }>(
    `/api/pay-periods${qs ? `?${qs}` : ""}`,
  );
}

export async function fetchPayPeriod(payPeriodId: string) {
  return apiFetch<{ data: PayPeriodDetail }>(`/api/pay-periods/${payPeriodId}`);
}

export async function fetchPayPeriodReview(
  payPeriodId: string,
  params?: {
    page?: number;
    pageSize?: number;
    q?: string;
    siteId?: string;
    crew?: string;
    workerStatus?: string;
  },
) {
  const search = new URLSearchParams();
  if (params?.page) search.set("page", String(params.page));
  if (params?.pageSize) search.set("pageSize", String(params.pageSize));
  if (params?.q) search.set("q", params.q);
  if (params?.siteId) search.set("siteId", params.siteId);
  if (params?.crew) search.set("crew", params.crew);
  if (params?.workerStatus) search.set("workerStatus", params.workerStatus);
  const qs = search.toString();
  return apiFetch<{ data: PayPeriodReview; meta: { total?: number; page: number; pageSize: number } }>(
    `/api/pay-periods/${payPeriodId}/review${qs ? `?${qs}` : ""}`,
  );
}

export async function createPayPeriod(input: CreatePayPeriodInput) {
  return apiFetch<{ data: PayPeriodDetail }>("/api/pay-periods", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function submitPayPeriodForReview(payPeriodId: string) {
  return apiFetch<{ data: PayPeriodDetail }>(`/api/pay-periods/${payPeriodId}/submit-review`, {
    method: "POST",
  });
}

export async function approvePayPeriod(payPeriodId: string) {
  return apiFetch<{ data: PayPeriodDetail }>(`/api/pay-periods/${payPeriodId}/approve`, {
    method: "POST",
  });
}

export async function lockPayPeriod(payPeriodId: string) {
  return apiFetch<{ data: PayPeriodDetail }>(`/api/pay-periods/${payPeriodId}/lock`, {
    method: "POST",
  });
}

export async function unlockPayPeriod(payPeriodId: string, input: UnlockPayPeriodInput) {
  return apiFetch<{ data: PayPeriodDetail }>(`/api/pay-periods/${payPeriodId}/unlock`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function exportPayPeriod(payPeriodId: string, input: ExportPayPeriodInput) {
  return apiFetch<{ data: ExportPayPeriodResponse }>(`/api/pay-periods/${payPeriodId}/export`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function recalculatePayPeriod(
  payPeriodId: string,
  options?: { async?: boolean; workerId?: string },
) {
  return apiFetch<{ data: RecalculatePayPeriodResponse }>(
    `/api/pay-periods/${payPeriodId}/recalculate`,
    {
      method: "POST",
      body: JSON.stringify(options ?? {}),
    },
  );
}

export async function approveWorkerInPeriod(payPeriodId: string, workerId: string) {
  return apiFetch<{ data: PayPeriodReview }>(
    `/api/pay-periods/${payPeriodId}/workers/${workerId}/approve`,
    { method: "POST" },
  );
}

export async function approveSiteInPeriod(payPeriodId: string, siteId: string) {
  return apiFetch<{ data: PayPeriodReview }>(
    `/api/pay-periods/${payPeriodId}/sites/${siteId}/approve`,
    { method: "POST" },
  );
}

export async function requestWorkerCorrection(
  payPeriodId: string,
  workerId: string,
  input: RequestCorrectionInput,
) {
  return apiFetch<{ data: PayPeriodReview }>(
    `/api/pay-periods/${payPeriodId}/workers/${workerId}/request-correction`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

export async function resolvePayPeriodException(exceptionId: string, reason?: string) {
  return apiFetch<{ data: { id: string; status: string } }>(
    `/api/exceptions/${exceptionId}/resolve`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}
